// Copyright ©️ 2026 eVolve MEP, LLC

import type { ReactNode } from 'react';

import type { StrongOmit } from '@evolvemep/api-ts';

import {
  ParentFormContext,
  useParentForm as useFormModal,
  useCreateParentFormProvider,
} from 'components/Mantine/Modals/useParentForm';
import { WrappedModal, type WrappedModalProps } from 'components/Mantine/Modals/WrappedModal';

/** Reexported for clarity in usage */
export { useFormModal };

export type FormModalProps = StrongOmit<
  WrappedModalProps,
  'tabs' | 'selectedTab' | 'onTabChange' | 'children' | 'onConfirm'
> & {
  type: 'submit';
  onClose: () => void;
} & {
  hideModalButtons?: false;
  startDirty?: boolean;
  children: ReactNode;
};

/**
 * A `WrappedModal` that collects form input.
 *
 * Children must include exactly one component that calls `useFormModal(form, onSubmit)`
 * to register its `useForm` instance.
 */
export const FormModal = <T,>({ startDirty, ...props }: FormModalProps) => {
  const { closeOnConfirm, disabled, loading, onClose } = props;
  const {
    contextValue,
    formSubmit,
    loading: wrappedLoading,
    disabled: wrappedDisabled,
  } = useCreateParentFormProvider<T>({ closeOnConfirm, onClose, startDirty, loading, disabled });

  return (
    <ParentFormContext value={contextValue}>
      <WrappedModal
        {...props}
        withCancelButton={undefined}
        loading={wrappedLoading}
        disabled={wrappedDisabled}
        closeOnConfirm={false}
        formSubmit={formSubmit}
      />
    </ParentFormContext>
  );
};
